import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { formatApiErrorDetail } from "../lib/api";
import { Anchor } from "lucide-react";

export default function AdminLoginPage() {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email.trim(), password);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) || t.admin.invalid
      );
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full border-b border-border bg-transparent px-0 py-3 focus:outline-none focus:border-primary transition-colors rounded-none text-foreground";

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex flex-col bg-background"
    >
      <div className="px-6 md:px-12 lg:px-20 py-8">
        <Link to="/" className="inline-flex items-center gap-2 group">
          <Anchor strokeWidth={1.4} className="h-5 w-5 text-primary" />
          <span className="font-display text-xl tracking-tight text-primary">
            World Commodity Supply
          </span>
        </Link>
      </div>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-2">
        <div className="hidden md:block relative">
          <img
            src="https://images.unsplash.com/photo-1568347877321-f8935c7dc5a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMHNoaXAlMjBvY2VhbiUyMHN1bnNldHxlbnwwfHx8fDE3ODE0MzIxNDd8MA&ixlib=rb-4.1.0&q=85"
            alt="MSC vessel"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1A120B]/60" />
        </div>

        <div className="flex items-center justify-center p-8 md:p-16">
          <form
            data-testid="admin-login-form"
            onSubmit={onSubmit}
            className="w-full max-w-md space-y-8"
          >
            <div>
              <p className="tracking-eyebrow text-xs uppercase text-secondary">
                {t.nav.admin}
              </p>
              <h1 className="font-display text-4xl md:text-5xl mt-2">
                {t.admin.login_title}
              </h1>
            </div>

            <div className="space-y-6">
              <div>
                <label className="text-[11px] tracking-eyebrow uppercase text-muted-foreground">
                  {t.admin.email}
                </label>
                <input
                  data-testid="admin-login-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-[11px] tracking-eyebrow uppercase text-muted-foreground">
                  {t.admin.password}
                </label>
                <input
                  data-testid="admin-login-password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>

            {error && (
              <p
                data-testid="admin-login-error"
                className="text-sm text-destructive"
              >
                {error}
              </p>
            )}

            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={submitting}
              className="w-full bg-primary text-primary-foreground px-8 py-4 text-sm tracking-wide hover:bg-[#2B1E17] transition-colors disabled:opacity-60"
            >
              {submitting ? t.admin.signing_in : t.admin.sign_in}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
